/**
 * useInfiniteScroll - Custom hook for infinite scroll with Intersection Observer
 *
 * Uses native IntersectionObserver to trigger load more when a sentinel element
 * becomes visible within the specified root margin.
 *
 * Uses callback ref pattern to properly handle component remounting (e.g., when
 * switching tabs) by recreating the IntersectionObserver when the sentinel
 * element is attached/detached from the DOM.
 */

import { useCallback, useRef } from 'react';

export interface UseInfiniteScrollOptions {
  onLoadMore: () => void | Promise<void>;
  hasMore: boolean;
  isLoading: boolean;
  rootMargin?: string;
}

export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
  rootMargin = '200px',
}: UseInfiniteScrollOptions) {
  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Disconnect previous observer if exists
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // If no node or shouldn't observe, return
      if (!node) return;

      // Create new observer
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasMore && !isLoading) {
            onLoadMore();
          }
        },
        { rootMargin }
      );

      observerRef.current.observe(node);
    },
    [onLoadMore, hasMore, isLoading, rootMargin]
  );

  return { sentinelRef };
}
